import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { Button, IconClose, IconArrowHead } from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  showThumbnails: false,
  showFullscreenButton: false,
  disableSwipe: true,
  autoPlay: false,
};
const ListingImageGallery = props => {
  const {
    intl,
    rootClassName = null,
    className = null,
    images,
    imageVariants,
    thumbnailVariants = null,
  } = props;
  const thumbVariants = thumbnailVariants || imageVariants;
  const items = images.map((img, i) => {
    const variants = img?.attributes?.variants || {};
    const pickUrl = vName => (variants[vName] ? variants[vName].url : null);
    const originalUrl =
      pickUrl(imageVariants[imageVariants.length - 1]) ||
      pickUrl(imageVariants[0]) ||
      Object.values(variants)?.[0]?.url ||
      '';
    const thumbUrl = pickUrl(thumbVariants[0]) || Object.values(variants)?.[0]?.url || '';

    return {
      original: originalUrl,
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: thumbUrl,
      image: img,
    };
  });
  // Use default image rendering from ReactImageGallery so it can measure height correctly.

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : (
      <button className={css.openFullscreen} onClick={onClick}>
        <FormattedMessage
          id="ListingImageGallery.viewImagesButton"
          values={{ count: images.length }}
        />
      </button>
    );
  };

  if (items.length === 0) {
    // Simple placeholder box when there are no images
    return <div className={css.noImage} style={{ width: '100%', minHeight: 320 }} />;
  }

  const classes = classNames(rootClassName || css.root, className);
  const galleryRef = useRef(null);
  const onImageLoad = () => {
    // Force layout recalculation to update wrapper height after image loads
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('resize'));
    }
  };

  return (
    <ReactImageGallery
      ref={galleryRef}
      additionalClass={classes}
      items={items}
      renderLeftNav={renderLeftNav}
      renderRightNav={renderRightNav}
      renderFullscreenButton={renderFullscreenButton}
      onImageLoad={onImageLoad}
      {...IMAGE_GALLERY_OPTIONS}
    />
  );
};



const { string, arrayOf, number } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),
  aspectWidth: number,
  aspectHeight: number,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ListingImageGallery);
